import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import { LoaderFunctionArgs } from "react-router";
import { Link, useLoaderData } from "react-router";
import { fetchPrices } from "~/lib/astrolescent";
import { setDbFromContext } from "~/lib/db";
import { getTokens, getTokensLimited } from "~/lib/models/tokens";
import { classNames, getIconUrl, onIconError, prettyValue, roundToDecimals } from "~/lib/util";
import { loader as pairsLoader } from "~/routes/cronjob.new-pairs";

const TOKENS_PER_PAGE = 25;

export async function loader({ context, request }: LoaderFunctionArgs) {
  setDbFromContext(context);

  // always check for new pairs first
  await pairsLoader({ context });

  const url = new URL(request.url);
  const page = +(url.searchParams.get("page") || 0);

  const tokens = await getTokensLimited(TOKENS_PER_PAGE, page * TOKENS_PER_PAGE);
  const allPrices = await fetchPrices();

  let prices = {};

  for (let token of tokens) {
    if (allPrices[token.address]) {
      prices[token.address] = allPrices[token.address];
    }
  }

  //   tokens.sort((a, b) => {
  //     if (a.tvlUSD < b.tvlUSD) {
  //       return 1;
  //     } else if (a.tvlUSD > b.tvlUSD) {
  //       return -1;
  //     }

  //     return 0;
  //   });

  return ({
    tokens,
    prices,
    page,
  });
}

export default function TokensIndexPage() {
  const { tokens, prices, page } = useLoaderData<typeof loader>();

  return (
    <div className="spacy-y-4 mx-4 mb-8 xl:mx-auto xl:w-3/5">
      <div className="flex items-center justify-between">
        <h1 className="py-4 text-left text-3xl font-bold lg:p-8 lg:text-4xl">Tokens</h1>
        <Link to="/launch" className="btn-white-small">
          Launch New Token
        </Link>
      </div>

      <div className="dark:bg-background-900 grow space-y-4 rounded-xl bg-white p-4 lg:rounded-2xl lg:p-8">
        <table className="min-w-full divide-y divide-gray-300">
          <thead>
            <tr>
              <th scope="col" className="text-primary-800 dark:text-primary-100 py-3.5 pl-0 pr-3 text-left text-sm font-semibold">
                Token
              </th>
              <th scope="col" className="text-primary-800 dark:text-primary-100 py-3.5 pl-4 pr-3 text-left text-sm font-semibold">
                Price
              </th>
              <th scope="col" className="text-primary-800 dark:text-primary-100 hidden py-3.5 pl-4 pr-3 text-left text-sm font-semibold lg:table-cell">
                24H %
              </th>
              <th scope="col" className="text-primary-800 dark:text-primary-100 hidden py-3.5 pl-4 pr-3 text-left text-sm font-semibold lg:table-cell">
                7D %
              </th>
              <th scope="col" className="text-primary-800 dark:text-primary-100 py-3.5 pl-4 pr-3 text-left text-sm font-semibold lg:hidden">
                Diff
              </th>
              <th scope="col" className="hidden py-3.5 pl-3 pr-0 lg:table-cell"></th>
            </tr>
          </thead>
          <tbody className="divide-primary-300 divide-y text-sm lg:text-base">
            {tokens.map((token) => {
              return (
                <tr key={token.address}>
                  <td className="py-4 pl-0 pr-3 font-bold">
                    <Link to={`/token/${token.address}`} className="flex items-center">
                      <img className="h-8 w-8 shrink-0 rounded-full bg-white" src={getIconUrl(token.iconUrl)} onError={(evt) => onIconError(evt)} alt="" />
                      <div className="font-sm ml-3 whitespace-nowrap">
                        {token.name}
                        <span className="text-primary-800 dark:text-primary-100 block font-normal lg:ml-2 lg:inline">{token.symbol}</span>
                      </div>
                    </Link>
                  </td>
                  <td className="py-4 pl-4 pr-3">{prettyValue(prices[token.address]?.tokenPriceXRD)} XRD</td>
                  <td className={classNames(prices[token.address]?.diff24H < 0 ? "text-red-500" : "text-green-500", "hidden py-4 pl-4 pr-3 lg:table-cell")}>
                    {roundToDecimals(prices[token.address]?.diff24H * 100, 2)}%
                  </td>
                  <td className={classNames(prices[token.address]?.diff7Days < 0 ? "text-red-500" : "text-green-500", "hidden py-4 pl-4 pr-3 lg:table-cell")}>
                    {roundToDecimals(prices[token.address]?.diff7Days * 100, 2)}%
                  </td>
                  <td className={classNames("py-4 pl-4 pr-3 text-xs lg:hidden")}>
                    <p>
                      24H: <span className={classNames(prices[token.address]?.diff24H < 0 ? "text-red-500" : "text-green-500")}>{roundToDecimals(prices[token.address]?.diff24H * 100, 2)}%</span>
                    </p>
                    <p>
                      7D: <span className={classNames(prices[token.address]?.diff7Days < 0 ? "text-red-500" : "text-green-500")}>{roundToDecimals(prices[token.address]?.diff7Days * 100, 2)}%</span>
                    </p>
                  </td>
                  {/* <td className="py-4 pl-4 pr-3">${prettyValue(token.tvlUSD)}</td> */}

                  <td className="hidden py-4 pl-3 pr-0 text-right text-sm font-medium lg:table-cell">
                    <Link to={`/token/${token.address}`} className="btn-primary-table">
                      Info
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="flex justify-center space-x-2 text-white">
          {page >= 1 ? (
            <Link to={`?page=${page - 1}`} className="btn-primary-small">
              <ChevronLeftIcon className="h-6 w-6" />
            </Link>
          ) : null}

          {tokens.length == TOKENS_PER_PAGE ? (
            <Link to={`?page=${page + 1}`} className="btn-primary-small">
              <ChevronRightIcon className="h-6 w-6" />
            </Link>
          ) : null}
        </div>
      </div>
    </div>
  );
}
